import {
  BookOpen,
  BriefcaseBusiness,
  ChevronDown,
  CircleHelp,
  CircleUserRound,
  FlaskConical,
  Gavel,
  GraduationCap,
  Menu,
  PanelLeft,
  PanelLeftClose,
  Scale,
  ScanText,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AppLink, useRouter } from "./router.jsx";
import {
  defaultSidebarLayout,
  persistSidebarLayoutPreference,
  readSidebarLayoutPreference,
  SIDEBAR_LAYOUT_STORAGE_KEY,
} from "./sidebar-layout.js";
import { SuiteLogo } from "./SuiteLogo.jsx";

const productIcons = {
  Cuenta: CircleUserRound,
  Praxis: BriefcaseBusiness,
  Instrumenta: ScanText,
  Peritia: Scale,
  Criterium: Gavel,
  Scientia: GraduationCap,
  Civita: Users,
  Lexia: BookOpen,
  Lab: FlaskConical,
};

const comingSoonProducts = [
  { name: "Peritia", disabled: true },
  { name: "Criterium", disabled: true },
  { name: "Scientia", disabled: true },
  { name: "Civita", disabled: true },
  { name: "Lexia", disabled: true },
  { name: "Lab", disabled: true },
];

function useMobileSuiteShell() {
  const [mobile, setMobile] = useState(() => (
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1024px)").matches
      : false
  ));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1024px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return mobile;
}

function useSidebarLayout() {
  const [responsiveLayout, setResponsiveLayout] = useState(() => (
    typeof window === "undefined"
      ? "fixed"
      : defaultSidebarLayout(window.innerWidth)
  ));
  const [preference, setPreference] = useState(() => (
    readSidebarLayoutPreference()
  ));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1439px)");
    const update = () => setResponsiveLayout(
      media.matches ? "collapsible" : "fixed",
    );
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const sync = (event) => {
      if (event?.type === "storage" && event.key !== SIDEBAR_LAYOUT_STORAGE_KEY) {
        return;
      }
      const next = readSidebarLayoutPreference();
      if (next) {
        try {
          window.localStorage.setItem(SIDEBAR_LAYOUT_STORAGE_KEY, next);
        } catch {
          // The cookie can still synchronize the preference across products.
        }
        setPreference(next);
      }
    };
    const syncWhenVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, []);

  const layout = preference || responsiveLayout;
  const updateLayout = (next) => {
    persistSidebarLayoutPreference(next);
    setPreference(next);
  };
  return [layout, updateLayout];
}

function ProductLinks({ products, productName, mobile = false }) {
  const configuredNames = new Set(products.map((product) => product.name));
  const visibleProducts = [
    ...products,
    ...comingSoonProducts.filter((product) => !configuredNames.has(product.name)),
  ];

  return (
    <nav
      className={mobile ? "suite-mobile-products" : "suite-product-nav"}
      aria-label="Productos de Tribunalea"
    >
      {mobile && <span className="suite-products-kicker">Productos</span>}
      <div
        className={`suite-product-list ${
          mobile ? "suite-mobile-product-grid" : ""
        }`}
      >
        {visibleProducts.map((product) => {
          const Icon = product.icon || productIcons[product.name] || ScanText;
          const active = product.name === productName;
          const className = `suite-product-link ${
            active ? "is-active" : ""
          } ${product.disabled ? "is-disabled" : ""}`;
          const label = product.disabled
            ? `${product.name}, próximamente`
            : product.name;

          if (product.disabled) {
            return (
              <button
                key={product.name}
                type="button"
                className={className}
                aria-label={label}
                title={`${product.name} · Próximamente`}
                disabled
              >
                <Icon size={mobile ? 18 : 20} />
                <span className="suite-product-copy">
                  <span>{product.name}</span>
                  <small>Próximamente</small>
                </span>
              </button>
            );
          }

          return (
            <a
              key={product.name}
              href={product.href}
              className={className}
              aria-current={active ? "page" : undefined}
              title={product.name}
              onClick={(event) => {
                if (active) event.preventDefault();
              }}
            >
              <Icon size={mobile ? 18 : 20} />
              <span className="suite-product-copy">
                <span>{product.name}</span>
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}

function AccountSettingsLink({ href, onClick }) {
  const content = (
    <>
      <Settings size={17} />
      <span>
        <strong>Configuración</strong>
        <small>Créditos y preferencias globales</small>
      </span>
    </>
  );
  if (/^https?:\/\//i.test(href)) {
    return (
      <a href={href} className="suite-account-settings-link" onClick={onClick}>
        {content}
      </a>
    );
  }
  return (
    <AppLink to={href} className="suite-account-settings-link" onClick={onClick}>
      {content}
    </AppLink>
  );
}

function DesktopAccountMenu({ accountControls, demo, settingsHref }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="suite-rail-account" ref={menuRef}>
      {open && (
        <div
          className="suite-account-popover"
          role="dialog"
          aria-label="Cuenta de Tribunalea"
        >
          <strong>Cuenta</strong>
          <AccountSettingsLink
            href={settingsHref}
            onClick={() => setOpen(false)}
          />
          <div className="suite-account-popover-controls">
            {accountControls || (
              <span className="suite-demo-account">
                <strong>{demo ? "Modo demostración" : "Cuenta no disponible"}</strong>
                <small>{demo ? "Configurá Clerk para iniciar sesión" : ""}</small>
              </span>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        className={`suite-product-link ${open ? "is-active" : ""}`}
        aria-label="Abrir cuenta"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        <CircleUserRound size={20} />
        <span>Cuenta</span>
      </button>
    </div>
  );
}

function CollapsibleNavigationItem({ item, onNavigate }) {
  const { path } = useRouter();
  const Icon = item.icon;
  const hasChildren = Boolean(item.children?.length);
  const routeActive = path === item.to
    || item.children?.some((child) => (
      path === child.to || path.startsWith(`${child.to}/`)
    ));
  const [expanded, setExpanded] = useState(() => Boolean(routeActive));

  useEffect(() => {
    if (routeActive) setExpanded(true);
  }, [routeActive, path]);

  return (
    <div
      className={`suite-nav-item ${expanded ? "is-expanded" : ""}`}
      key={item.to}
    >
      <div className="suite-nav-link-row">
        <AppLink
          to={item.to}
          className="suite-nav-link"
          onClick={(event) => {
            if (hasChildren) setExpanded(true);
            onNavigate(event);
          }}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </AppLink>
        {hasChildren && (
          <button
            type="button"
            className="suite-nav-toggle"
            aria-label={`${expanded ? "Contraer" : "Desplegar"} ${item.label}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>
      {hasChildren && expanded ? (
        <div className="suite-nav-children">
          {item.children.map((child) => (
            <AppLink
              key={child.to}
              to={child.to}
              exact={child.exact ?? true}
              className="suite-nav-child-link"
              onClick={onNavigate}
            >
              <span className="suite-nav-child-marker" />
              <span>{child.label}</span>
            </AppLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProductShell({
  productName,
  products = [],
  navigation = [],
  portalUrl,
  accountControls,
  demo = false,
  accountSettingsHref = "/creditos",
  children,
  supportEmail = "soporte@tribunalea.com",
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedAfterNavigate, setCollapsedAfterNavigate] = useState(false);
  const mobile = useMobileSuiteShell();
  const [sidebarLayout, setSidebarLayout] = useSidebarLayout();
  const collapsibleSidebar = sidebarLayout === "collapsible";

  useEffect(() => {
    if (!collapsibleSidebar) setCollapsedAfterNavigate(false);
  }, [collapsibleSidebar]);

  const handleSidebarNavigate = (event) => {
    event?.currentTarget?.blur?.();
    setMobileOpen(false);
    if (collapsibleSidebar && !mobile) setCollapsedAfterNavigate(true);
  };

  return (
    <div
      className={`suite-shell ${
        collapsibleSidebar ? "suite-shell--sidebar-collapsible" : ""
      }`}
    >
      {!mobile && (
        <aside className="suite-rail" aria-label="Navegación de Tribunalea">
          <a
            className="suite-rail-brand"
            href={portalUrl}
            aria-label="Ir al portal de Tribunalea"
            title="Portal de Tribunalea"
          >
            <SuiteLogo productName={productName} compact />
          </a>
          <ProductLinks products={products} productName={productName} />
          <DesktopAccountMenu
            accountControls={accountControls}
            demo={demo}
            settingsHref={accountSettingsHref}
          />
        </aside>
      )}

      <aside
        className={`suite-sidebar ${
          collapsibleSidebar ? "suite-sidebar--collapsible" : ""
        } ${collapsedAfterNavigate ? "is-navigation-collapsed" : ""
        } ${mobileOpen ? "is-open" : ""}`}
        onPointerEnter={() => {
          if (collapsedAfterNavigate) setCollapsedAfterNavigate(false);
        }}
      >
        <div className="suite-sidebar-top">
          <SuiteLogo productName={productName} />
          <button
            type="button"
            className="suite-icon-button suite-sidebar-close"
            aria-label="Cerrar navegación"
            onClick={() => setMobileOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {mobile && (
          <ProductLinks
            products={products}
            productName={productName}
            mobile
          />
        )}

        {demo && (
          <div className="suite-demo-workspace">
            <span>
              <strong>Vista de demostración</strong>
              <small>Sin identidad conectada</small>
            </span>
          </div>
        )}

        <nav
          className="suite-main-nav"
          aria-label={`Navegación de ${productName}`}
        >
          {navigation.map((section, sectionIndex) => (
            <div
              className={`suite-nav-section ${
                sectionIndex ? "suite-nav-section--spaced" : ""
              }`}
              key={section.label}
            >
              <span className="suite-nav-kicker">{section.label}</span>
              {section.items.map((item) => (
                <CollapsibleNavigationItem
                  key={item.to}
                  item={item}
                  onNavigate={handleSidebarNavigate}
                />
              ))}
            </div>
          ))}
        </nav>

        <div className="suite-sidebar-footer">
          <button
            type="button"
            className="suite-sidebar-layout-button"
            title={collapsibleSidebar
              ? "Cambiar a barra lateral fija"
              : "Cambiar a barra lateral contraíble"}
            aria-label={collapsibleSidebar
              ? "Cambiar a barra lateral fija"
              : "Cambiar a barra lateral contraíble"}
            onClick={() => setSidebarLayout(
              collapsibleSidebar ? "fixed" : "collapsible",
            )}
          >
            {collapsibleSidebar
              ? <PanelLeft size={17} />
              : <PanelLeftClose size={17} />}
            <span>
              <strong>{collapsibleSidebar ? "Contraíble" : "Fija"}</strong>
              <small>{collapsibleSidebar
                ? "Se expande al pasar el cursor"
                : "Siempre permanece abierta"}</small>
            </span>
          </button>
          <a href={`mailto:${supportEmail}`} className="suite-support-link">
            <CircleHelp size={17} />
            <span>Ayuda y soporte</span>
          </a>
          {mobile && (
            <div className="suite-account-row">
              <AccountSettingsLink
                href={accountSettingsHref}
                onClick={() => setMobileOpen(false)}
              />
              {accountControls || (
                <span className="suite-demo-account">
                  <strong>Modo demostración</strong>
                  <small>Configurá Clerk para iniciar sesión</small>
                </span>
              )}
            </div>
          )}
        </div>
      </aside>

      {mobileOpen && (
        <button
          className="suite-sidebar-backdrop"
          aria-label="Cerrar navegación"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main className="suite-content-shell">
        <header className="suite-mobile-header">
          <button
            className="suite-icon-button"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir navegación"
          >
            <Menu size={22} />
          </button>
          <a
            className="suite-mobile-brand-link"
            href={portalUrl}
            aria-label="Ir al portal de Tribunalea"
          >
            <SuiteLogo productName={productName} />
          </a>
          <span className="suite-mobile-header-spacer" />
        </header>
        {children}
      </main>
    </div>
  );
}
